// @flow
import type { Asset, } from '@haaretz/htz-components';
import { AdSlot, GraphController, Grid, GridItem, Query, SeoData, useMedia, } from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import AdditionalInfo from '../../components/AdditionalInfo/AdditionalInfo';
import AssetStaticGraphs from '../../components/AssetStaticGraphs/AssetStaticGraphs';
import PageRow from '../../components/PageRow/PageRow';
import QuoteInfoTable from '../../components/QuotePageComponents/QuoteInfoTable/QuoteInfoTable';
import QuoteSummary from '../../components/QuotePageComponents/QuoteSummary/QuoteSummary';
import RelatedAssets from '../../components/QuotePageComponents/RelatedAssets/RelatedAssets';
import ShareHolders from '../../components/QuotePageComponents/ShareHolders/ShareHolders';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import StockQuery from '../queries/stockData.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      assetId: string,
      section: string,
    },
    asPath: string,
  },
  responseEnd: () => void,
  responseWriteHead: (statusCode: number, headers: { [key: string]: string | number | boolean, }) => void,
};

function stock({
  url: {
    query: { assetId, section, },
    asPath,
  },
  responseEnd,
  responseWriteHead,
}: Props): Node {
  const isMobile = useMedia({ query: { from: 's', }, });
  const adUnits = useAllAdUnits();
  const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section} assetId={assetId}>
      <Query query={StockQuery} variables={{ assetId, }}>
        {({ loading, error, data, }) => {
          if (error) return null;
          if (loading) return null;
          if (!data.assets?.length) return null;

          const type = data.assets[0]?.type?.toLowerCase();

          if (type && section !== type) {
            responseWriteHead(301, { Location: encodeURI(asPath.replace(section, type)), });
            responseEnd();

            return null;
          }

          const asset: Asset = data.assets[0];
          const {
            name,
            value,
            USDValue,
            symbol,
            changePercentage,
            numeralChange,
            subType,
            assetNumber,
            assetSummary,
            lastTradeTime,
            relatedAssets,
            shareHolders,
            ...staticGraphsData
          } = asset;

          const isForeign = USDValue !== null;

          return (
            <FelaTheme>
              {theme => (
                <Fragment>
                  <SeoData
                    title={`מניית (${name}) ${assetNumber} - מניה TheMarker Finance`}
                    description={`כל המידע על  מניית ${name} ${assetNumber}: נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance`}
                    path={asPath}
                  />
                  <PageRow lines={2} miscStyles={{ position: 'relative', }}>
                    <RowItem
                      title={name}
                      miscStyles={{
                        ...theme.type(5),
                      }}
                    />
                    {
                      assetSummary
                        ? (
                          <AdditionalInfo
                            title="למידע נוסף על המניה"
                            info={assetSummary}
                            miscStyles={{
                              position: 'absolute',
                              left: '0',
                              bottom: '1rem',
                              zIndex: '2',
                              width: '41rem',
                            }}
                          />
                        )
                        : null
                    }
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      marginBottom: [
                        { until: 'l', value: '0', },
                        { from: 'l', value: '2rem', },
                      ],
                    }}
                  >
                    <QuoteSummary
                      valueData={[
                        { title: 'שער', value: value ? value.toString() : '', },
                        { title: '% שינוי', value: changePercentage, },
                        { title: isForeign ? 'שינוי בדולרים' : 'שינוי באג׳', value: numeralChange, },
                      ]}
                      date={{ title: 'נכון ל:', value: lastTradeTime, }}
                      assetInfo={[
                        { title: 'סוג נייר:', value: subType, },
                        ...(isForeign
                          ? [{ title: 'סימול:', value: symbol, },]
                          : [{ title: 'מספר נייר:', value: assetNumber, },]
                        ),
                      ]}
                    />
                  </PageRow>
                  {relatedAssets
                    ? (
                      <PageRow
                        miscStyles={{
                          marginBottom: '2rem',
                        }}
                      >
                        <RelatedAssets assets={relatedAssets} />
                      </PageRow>
                    )
                    : null
                  }
                  <PageRow>
                    {
                      isMobile
                        ? <GraphController assetId={assetId} width={900} />
                        // : <MobileGraphController assetId={assetId} />
                        : null
                    }
                  </PageRow>
                  <PageRow>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <QuoteInfoTable
                        id={assetId}
                        miscStyles={{ marginBottom: [{ until: 'l', value: '4rem', },], }}
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 3, },
                        ]}
                        title="נתוני המסחר"
                        tradingStatus
                        fixed
                        fields={[
                          { name: 'baseValue', display: 'שער בסיס', },
                          { name: 'openingValue', display: 'שער פתיחה', },
                          { name: 'value', display: 'שער אחרון', },
                          { name: 'dailyLow', display: 'נמוך יומי', },
                          { name: 'dailyHigh', display: 'גבוה יומי', },
                          { name: 'marketCap', display: 'שווי שוק', },
                        ]}
                      />
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 2 / 3, },
                        ]}
                      >
                        <AssetStaticGraphs {...staticGraphsData} isForeign={isForeign} />
                      </GridItem>
                    </Grid>
                  </PageRow>
                  {
                    !isForeign
                      ? (
                        <PageRow>
                          <Grid
                            gutter={2}
                            miscStyles={{
                              paddingStart: '0rem',
                              paddingEnd: '0rem',
                            }}
                          >
                            <GridItem
                              width={[
                                { until: 'l', value: 1, },
                                { from: 'l', value: 1 / 3, },
                              ]}
                              miscStyles={{
                                order: [{ until: 'l', value: 2, },],
                              }}
                            >
                              <RowItem
                                title="תוכן מקודם"
                                miscStyles={{
                                  ...theme.type(0),
                                }}
                              >
                                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.kodakright'], adUnitPathTemplate)} />
                              </RowItem>
                            </GridItem>
                            <GridItem
                              width={[
                                { until: 'l', value: 1, },
                                { from: 'l', value: 2 / 3, },
                              ]}
                            >
                              <RowItem title="בעלי עניין">
                                {shareHolders ? <ShareHolders data={shareHolders} /> : null}
                              </RowItem>
                            </GridItem>
                          </Grid>
                        </PageRow>
                      )
                      : null
                  }
                  <FelaComponent
                    style={{
                      display: 'block',
                      extend: [
                        theme.mq(
                          { from: 'l', },
                          {
                            display: 'none',
                          }
                        ),
                      ],
                    }}
                  >
                    <PageRow>
                      <RowItem>
                        <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
                      </RowItem>
                    </PageRow>
                  </FelaComponent>
                </Fragment>
              )}
            </FelaTheme>
          );
        }}
      </Query>
    </MainLayout>
  );
}

export default stock;
